/**
 * ClassificationsPanel - Admin Pending Classifications Queue
 * 
 * Surfaces SUGGESTED conditions for admin review.
 * Admin can: CONFIRM, RECLASSIFY, REJECT
 * 
 * Features:
 * - Count badges (SUGGESTED/CONFIRMED/REJECTED)
 * - Filters: responsibility, confidence, source engine, date range
 * - Queue card with deal info, confidence %, trigger source
 * - Explain dropdown (why system suggested this)
 * - One-click admin actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, CheckCircle2, XCircle, RefreshCw, 
  ChevronDown, ChevronUp, Filter, Clock, Building2,
  User, Target, Percent, Zap, FileText, RotateCcw,
  AlertCircle, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const RESPONSIBILITY_COLORS = {
  BUYER: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
  SELLER: 'bg-amber-600/20 text-amber-400 border-amber-600/30',
  BOTH: 'bg-purple-600/20 text-purple-400 border-purple-600/30',
  BANK: 'bg-cyan-600/20 text-cyan-400 border-cyan-600/30',
  THIRD_PARTY: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
  EXTERNAL: 'bg-rose-600/20 text-rose-400 border-rose-600/30',
  UNDETERMINED: 'bg-white/10 text-white/60 border-white/20'
};

const SOURCE_ENGINE_ICONS = {
  DEADLINE: Clock,
  PAYMENT: Target,
  AMENDMENT: FileText,
  DEAL: Building2
};

const CONDITION_TYPES = [
  { value: 'DELAY_FLAG', label: 'Delay Flag' },
  { value: 'SELLER_DELAY', label: 'Seller Delay' },
  { value: 'BUYER_DELAY', label: 'Buyer Delay' },
  { value: 'FUNDS_NOT_READY', label: 'Funds Not Ready' },
  { value: 'BANK_APPROVAL_PENDING', label: 'Bank Approval Pending' },
  { value: 'MORTGAGE_REJECTION', label: 'Mortgage Rejection' },
  { value: 'NOC_PENDING', label: 'NOC Pending' },
  { value: 'DOCUMENT_MISSING', label: 'Document Missing' },
  { value: 'AMENDMENT_REQUIRED', label: 'Amendment Required' },
  { value: 'TRUSTEE_APPOINTMENT_DELAY', label: 'Trustee Delay' },
  { value: 'HANDOVER_DELAY', label: 'Handover Delay' },
  { value: 'DEADLINE_EXCEEDED', label: 'Deadline Exceeded' }
];

const RESPONSIBILITIES = [
  { value: 'BUYER', label: 'Buyer' },
  { value: 'SELLER', label: 'Seller' },
  { value: 'BOTH', label: 'Both Parties' },
  { value: 'BANK', label: 'Bank' },
  { value: 'THIRD_PARTY', label: 'Third Party' },
  { value: 'EXTERNAL', label: 'External' },
  { value: 'UNDETERMINED', label: 'Undetermined' }
];

const formatTimestamp = (ts) => {
  if (!ts) return 'N/A';
  const date = new Date(ts);
  const now = new Date();
  const diff = now - date;
  
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============ CLASSIFICATION CARD COMPONENT ============

const ClassificationCard = ({ condition, onConfirm, onReject, onReclassify }) => {
  const [expanded, setExpanded] = useState(false);
  const [showReclassifyDialog, setShowReclassifyDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [reclassifyData, setReclassifyData] = useState({ new_type: '', new_responsibility: '', reason: '' });
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  
  const SourceIcon = SOURCE_ENGINE_ICONS[condition.source_engine] || Zap;
  const confidencePercent = Math.round((condition.classification_confidence || 0) * 100);
  const responsibilityClass = RESPONSIBILITY_COLORS[condition.suggested_responsibility] || RESPONSIBILITY_COLORS.UNDETERMINED;
  
  // Extract breakdown from details
  const breakdown = condition.details?.suggestion?.confidence_breakdown || {};
  const signals = condition.details?.suggestion?.signals || [];
  const contradictions = condition.details?.suggestion?.contradictions || [];
  
  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(condition.condition_id);
      toast.success('Classification confirmed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setLoading(true);
    try {
      await onReject(condition.condition_id, rejectReason);
      toast.success('Classification rejected');
      setShowRejectDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReclassify = async () => {
    if (!reclassifyData.new_type) {
      toast.error('New type is required');
      return;
    }
    setLoading(true);
    try {
      await onReclassify(condition.condition_id, reclassifyData);
      toast.success('Classification updated');
      setShowReclassifyDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reclassify');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div 
      className="border border-white/10 rounded-lg bg-white/5 hover:bg-white/8 transition-all"
      data-testid={`classification-card-${condition.condition_id}`}
    >
      {/* Main Card Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge 
                variant="outline" 
                className={responsibilityClass}
              >
                {condition.suggested_responsibility || 'UNKNOWN'}
              </Badge>
              <Badge variant="outline" className="border-white/20 text-white/60">
                <SourceIcon className="w-3 h-3 mr-1" />
                {condition.source_engine}
              </Badge>
              {/* Confidence with signal count */}
              <Badge 
                variant="outline" 
                className={`${confidencePercent >= 80 ? 'border-green-600/50 text-green-400' : confidencePercent >= 60 ? 'border-gold-600/50 text-gold-400' : 'border-red-600/50 text-red-400'}`}
                title={`Raw: ${(breakdown.score_raw * 100 || 0).toFixed(0)}% × Coverage: ${(breakdown.coverage_multiplier * 100 || 0).toFixed(0)}% × Penalty: ${(breakdown.contradiction_penalty * 100 || 0).toFixed(0)}%`}
              >
                <Percent className="w-3 h-3 mr-1" />
                {confidencePercent}% ({signals.length} signals, {contradictions.length} contradictions)
              </Badge>
              {/* Low coverage warning */}
              {breakdown.capped_due_to_low_coverage && (
                <Badge variant="outline" className="border-orange-600/50 text-orange-400 text-xs">
                  Low Evidence
                </Badge>
              )}
            </div>
            
            <h4 className="font-medium text-white truncate">
              {condition.title?.replace('[PENDING REVIEW] ', '') || condition.suggested_type?.replace(/_/g, ' ')}
            </h4>
            
            <p className="text-sm text-muted-foreground mt-1 truncate">
              Deal: {condition.deal_id?.substring(0, 8)}... • {formatTimestamp(condition.created_at)}
            </p>
            
            <p className="text-sm text-muted-foreground mt-1">
              Trigger: <span className="text-white/80">{condition.source_event?.replace(/_/g, ' ')}</span>
            </p>
          </div>
          
          {/* Right: Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="border-green-600/50 text-green-400 hover:bg-green-950/30"
              onClick={handleConfirm}
              disabled={loading}
              data-testid={`confirm-btn-${condition.condition_id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Confirm
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-blue-600/50 text-blue-400 hover:bg-blue-950/30"
              onClick={() => setShowReclassifyDialog(true)}
              disabled={loading}
              data-testid={`reclassify-btn-${condition.condition_id}`}
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reclassify
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-red-600/50 text-red-400 hover:bg-red-950/30"
              onClick={() => setShowRejectDialog(true)}
              disabled={loading}
              data-testid={`reject-btn-${condition.condition_id}`}
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject
            </Button>
          </div>
        </div>
      </div>
      
      {/* Expandable Explanation - Shows Signals and Contradictions */}
      <Collapsible open={expanded} onOpenChange={setExpanded}>
        <CollapsibleTrigger asChild>
          <button 
            className="w-full px-4 py-2 border-t border-white/10 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-white hover:bg-white/5 transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            {expanded ? 'Hide Evidence' : 'Show Evidence Basis'}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 pb-4 pt-2 border-t border-white/10 bg-white/3 space-y-4">
            
            {/* Reason */}
            <div>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Classification Reason</span>
              <p className="text-sm text-white/80 mt-1">{condition.classification_reason || condition.description}</p>
            </div>
            
            {/* Signals */}
            {signals.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Evidence Signals ({signals.length})</span>
                <div className="mt-2 space-y-1">
                  {signals.map((signal, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">{signal.explain}</span>
                      <span className="text-muted-foreground ml-auto flex-shrink-0">
                        ({(signal.value * 100).toFixed(0)}% × {(signal.weight * 100).toFixed(0)}w)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Contradictions */}
            {contradictions.length > 0 && (
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Contradictions ({contradictions.length})</span>
                <div className="mt-2 space-y-1">
                  {contradictions.map((contradiction, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/80">{contradiction.explain}</span>
                      <span className="text-muted-foreground ml-auto flex-shrink-0">
                        ({(contradiction.value * 100).toFixed(0)}% × {(contradiction.weight * 100).toFixed(0)}w)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Confidence Breakdown */}
            {breakdown.score_raw !== undefined && (
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Confidence Calculation</span>
                <div className="mt-2 text-xs text-muted-foreground font-mono bg-black/20 p-2 rounded">
                  Raw Score: {(breakdown.score_raw * 100).toFixed(1)}% × 
                  Coverage: {(breakdown.coverage_multiplier * 100).toFixed(0)}% × 
                  Penalty: {(breakdown.contradiction_penalty * 100).toFixed(0)}% = 
                  <span className="text-white ml-1">{confidencePercent}%</span>
                  {breakdown.capped_due_to_low_coverage && <span className="text-orange-400 ml-2">(capped: low evidence)</span>}
                  {breakdown.capped_at_max && <span className="text-blue-400 ml-2">(capped at 95% max)</span>}
                </div>
              </div>
            )}
            
            {/* Raw Evidence */}
            {condition.details?.suggestion?.evidence && (
              <div>
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Raw Evidence Data</span>
                <pre className="text-xs text-white/60 mt-1 bg-black/20 p-2 rounded overflow-x-auto">
                  {JSON.stringify(condition.details.suggestion.evidence, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
      
      {/* Reclassify Dialog */}
      <Dialog open={showReclassifyDialog} onOpenChange={setShowReclassifyDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle>Reclassify Condition</DialogTitle>
            <DialogDescription>
              Change the classification type and responsibility.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">New Type</label>
              <Select 
                value={reclassifyData.new_type} 
                onValueChange={(v) => setReclassifyData(d => ({ ...d, new_type: v }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {CONDITION_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">New Responsibility</label>
              <Select 
                value={reclassifyData.new_responsibility} 
                onValueChange={(v) => setReclassifyData(d => ({ ...d, new_responsibility: v }))}
              >
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue placeholder="Select responsibility" />
                </SelectTrigger>
                <SelectContent>
                  {RESPONSIBILITIES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason (optional)</label>
              <Textarea 
                placeholder="Why are you changing this classification?"
                value={reclassifyData.reason}
                onChange={(e) => setReclassifyData(d => ({ ...d, reason: e.target.value }))}
                className="bg-white/5 border-white/10"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReclassifyDialog(false)}>Cancel</Button>
            <Button onClick={handleReclassify} disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="bg-card border-white/10">
          <DialogHeader>
            <DialogTitle>Reject Classification</DialogTitle>
            <DialogDescription>
              This will mark the condition as expired. A reason is required.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              placeholder="Why is this a false positive?"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="bg-white/5 border-white/10"
              required
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} disabled={loading || !rejectReason.trim()}>
              {loading ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ MAIN COMPONENT ============

const ClassificationsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingConditions, setPendingConditions] = useState([]);
  const [summary, setSummary] = useState({ pending_classifications: 0, by_status: {} });
  const [responsibilityBreakdown, setResponsibilityBreakdown] = useState({});
  
  // Filters
  const [filters, setFilters] = useState({
    responsibility: 'all',
    source_engine: 'all',
    min_confidence: 0
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, summaryRes, breakdownRes] = await Promise.all([
        api.get('/v2/conditions/pending-classifications'),
        api.get('/v2/conditions/summary'),
        api.get('/v2/conditions/responsibility-breakdown')
      ]);
      
      setPendingConditions(pendingRes.data.pending_classifications || []);
      setSummary(summaryRes.data);
      setResponsibilityBreakdown(breakdownRes.data.responsibility_breakdown || {});
    } catch (error) {
      console.error('Failed to fetch classifications:', error);
      toast.error('Failed to load classifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };
  
  // Admin actions
  const handleConfirm = async (conditionId) => {
    await api.post(`/v2/conditions/${conditionId}/confirm-classification?admin_id=admin`, {
      action: 'CONFIRM'
    });
    fetchData();
  };
  
  const handleReject = async (conditionId, reason) => {
    await api.post(`/v2/conditions/${conditionId}/confirm-classification?admin_id=admin`, {
      action: 'REJECT',
      reason
    });
    fetchData();
  };
  
  const handleReclassify = async (conditionId, data) => {
    await api.post(`/v2/conditions/${conditionId}/confirm-classification?admin_id=admin`, {
      action: 'RECLASSIFY',
      new_type: data.new_type,
      new_responsibility: data.new_responsibility,
      reason: data.reason
    });
    fetchData();
  };
  
  // Filter conditions
  const filteredConditions = pendingConditions.filter(c => {
    if (filters.responsibility !== 'all' && c.suggested_responsibility !== filters.responsibility) return false;
    if (filters.source_engine !== 'all' && c.source_engine !== filters.source_engine) return false;
    if ((c.classification_confidence || 0) * 100 < filters.min_confidence) return false;
    return true;
  });
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="classifications-panel">
      
      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-gold-600/30 bg-gold-950/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold text-gold-400">{summary.pending_classifications || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-gold-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-green-600/30 bg-green-950/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Confirmed</p>
                <p className="text-2xl font-bold text-green-400">
                  {Object.values(responsibilityBreakdown).reduce((sum, r) => sum + (r.confirmed || 0), 0)}
                </p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-red-600/30 bg-red-950/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical</p>
                <p className="text-2xl font-bold text-red-400">{summary.critical_conditions || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-400/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-600/30 bg-blue-950/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Conditions</p>
                <p className="text-2xl font-bold text-blue-400">{summary.total || 0}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Responsibility Breakdown */}
      {Object.keys(responsibilityBreakdown).length > 0 && (
        <Card className="border-white/10 bg-white/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Responsibility Distribution
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="flex flex-wrap gap-2">
              {Object.entries(responsibilityBreakdown).map(([responsibility, counts]) => (
                <Badge 
                  key={responsibility}
                  variant="outline"
                  className={RESPONSIBILITY_COLORS[responsibility]}
                >
                  {responsibility}: {counts.total} ({counts.pending} pending)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Queue Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-gold-400" />
          Pending Classifications Queue
          <Badge variant="outline" className="ml-2 border-gold-600/50 text-gold-400">
            {filteredConditions.length}
          </Badge>
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-white/20"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-white/20"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      {showFilters && (
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Responsibility</label>
                <Select 
                  value={filters.responsibility} 
                  onValueChange={(v) => setFilters(f => ({ ...f, responsibility: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {RESPONSIBILITIES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Source Engine</label>
                <Select 
                  value={filters.source_engine} 
                  onValueChange={(v) => setFilters(f => ({ ...f, source_engine: v }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="DEADLINE">Deadline</SelectItem>
                    <SelectItem value="PAYMENT">Payment</SelectItem>
                    <SelectItem value="AMENDMENT">Amendment</SelectItem>
                    <SelectItem value="DEAL">Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Min Confidence: {filters.min_confidence}%</label>
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={filters.min_confidence}
                  onChange={(e) => setFilters(f => ({ ...f, min_confidence: parseInt(e.target.value) }))}
                  className="bg-white/5"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Queue List */}
      <div className="space-y-3">
        {filteredConditions.length === 0 ? (
          <Card className="border-white/10 bg-white/5">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-400/50 mx-auto mb-4" />
              <p className="text-lg font-medium text-green-400">All Caught Up!</p>
              <p className="text-muted-foreground mt-1">No pending classifications to review</p>
            </CardContent>
          </Card>
        ) : (
          <ScrollArea className="h-[500px]">
            <div className="space-y-3 pr-4">
              {filteredConditions.map(condition => (
                <ClassificationCard
                  key={condition.condition_id}
                  condition={condition}
                  onConfirm={handleConfirm}
                  onReject={handleReject}
                  onReclassify={handleReclassify}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

export default ClassificationsPanel;
